<template>
  <div class="campaign_analytics_dialog">
    <el-dialog
      custom-class="with-title x-large"
      :visible="visible"
      :before-close="handleBeforeClose"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div class="custom-title" layout layout-align="start-center" slot="title">
        {{ 'キャンペーン分析' }}
        <icon
          class="mr-x1 ml-x2"
          :name="campaignName"
          :pictureUrl="campaignMeta.pictureUrl"
          :iconType="campaignMeta.iconType"
        ></icon>
        <div>{{ campaignName }}</div>
      </div>
      <div v-loading="loading">
        <div v-if="isCampaignFinalCollection">
          <div class="mb-x2">
            <div class="message message-info">
              {{ '分析データの最終集計中です。' }}<br />{{ collectCompletedAt }}
              {{ 'ごろまでに最終的なデータが反映されます。応募件数により、反映が遅れる場合があります。' }}
            </div>
          </div>
        </div>
        <div v-else-if="isCampaignCollectionCompleted">
          <div class="mb-x2">
            <div class="message message-info">{{ '分析データの最終集計が完了しました。' }}</div>
          </div>
        </div>
        <el-row class="mb-x2" :gutter="20">
          <el-col :span="8">
            <el-select class="prize-select mr-x2" v-model="prizeId" @change="handlePrizeChange">
              <el-option
                v-for="(prize, index) in prizeOptions"
                :value="prize.id"
                :label="prize.name"
                :key="index"
              ></el-option>
            </el-select>
          </el-col>
          <el-col class="date-time-picker" :span="8">
            <analytics-period-picker
              v-model="dateTimeRange"
              :campaign-start-datetime="campaignStartDatetime"
              :campaign-end-datetime="campaignEndDatetime"
              @change="handleDateRangeChange"
            ></analytics-period-picker>
          </el-col>
        </el-row>
        <el-row class="mb-x2" :gutter="20">
          <el-col :span="8">
            <analytics-total
              :metric="'応募数'"
              :value="entryCount"
              tooltip="キャンペーンへの応募数です。この数値には重複応募も含まれます。"
            ></analytics-total>
          </el-col>
          <el-col :span="8">
            <analytics-total
              :metric="'応募者数'"
              :value="applicantCount"
              tooltip="キャンペーンへ応募したアカウントの数です。"
            ></analytics-total>
          </el-col>
          <el-col :span="8">
            <analytics-total
              :metric="'新規応募者数'"
              :value="newApplicantCount"
              tooltip="同一開催アカウントで過去に実施したキャンペーンに応募した実績のない応募者の数です。"
            ></analytics-total>
          </el-col>
        </el-row>
        <el-row class="mb-x3" :gutter="20">
          <el-col :span="8">
            <analytics-total
              :metric="'フォロワー増減数'"
              :value="followerUpDown"
              :isUpDown="true"
              tooltip="キャンペーン開催アカウントのフォロワー数の増減です。"
            ></analytics-total>
          </el-col>
          <el-col :span="8">
            <analytics-total :metric="'当選数'" :value="winnerCount"></analytics-total>
          </el-col>
        </el-row>
        <el-tabs v-model="activeTab">
          <el-tab-pane :label="'キャンペーン'" name="campaigns" class="graph-container">
            <div class="mb-x3" layout layout-align="space-between-center">
              <div layout>
                <div layout="column">
                  <label class="nav_label">{{ '値1' }}</label>
                  <el-select v-model="value1" @change="handleMetricChange('value1')">
                    <el-option
                      v-for="(item, index) in metricOptions"
                      :key="index"
                      :label="item.label"
                      :value="item.value"
                    ></el-option>
                  </el-select>
                </div>
                <div class="ml-x2" layout="column">
                  <label class="nav_label">{{ '値2' }}</label>
                  <el-select v-model="value2" @change="handleMetricChange('value2')">
                    <el-option
                      v-for="(item, index) in metricOptions"
                      :key="index"
                      :label="item.label"
                      :value="item.value"
                    ></el-option>
                  </el-select>
                </div>
              </div>
              <div>
                <div class="nav_label">&nbsp;</div>
                <el-switch
                  v-model="isBeginAtZero"
                  :active-text="'0から表示'"
                  :active-value="true"
                  :inactive-value="false"
                  @change="handleBeginAtZeroChange"
                ></el-switch>
              </div>
              <div>
                <div class="nav_label">&nbsp;</div>
                <div layout layout-align="space-around-center">
                  <el-button-group>
                    <el-button
                      :type="interval === item.value ? 'primary' : 'default'"
                      v-for="item in intervals"
                      :key="item.value"
                      @click="handleIntervalChange(item.value)"
                    >
                      {{ item.text }}
                    </el-button>
                  </el-button-group>
                  <div class="ml-x1">
                    <el-tooltip placement="top" :content="'CSVダウンロード'">
                      <csv-download :download="downloadCsv" :text="''"></csv-download>
                    </el-tooltip>
                  </div>
                </div>
              </div>
            </div>
            <div class="message message-info" v-if="!hasAnalyticsData">{{ 'データがありません。' }}</div>
            <div class="message message-info" v-else-if="value1 === '' && value2 === ''">
              {{ '値1または値2を選択してください。' }}
            </div>
            <div class="body" v-else>
              <analytics-chart-vertical
                ref="chart"
                :data="chartData"
                :xAxis="labels"
                :interval="interval"
                :isBeginAtZero="isBeginAtZero"
              ></analytics-chart-vertical>
            </div>
          </el-tab-pane>
          <el-tab-pane :label="'アンケート'" name="questionnaire" class="graph-container">
            <InstagramInstantwinCampaignAnalyticsEnqueteTab
              :prize-id="prizeId"
              :campaign-name="campaignName"
              :questions="enqueteQuestions"
              :selected-id="enqueteSelectedId"
              :answers="enqueteAnswers"
              :total-count="enqueteTotalCount"
              :loading="enqueteLoading"
              @select-question="handleEnqueteQuestionChange"
            />
          </el-tab-pane>
          <el-tab-pane :label="'メッセージごとの送信数'" name="message_send_counts" class="graph-container">
            <InstagramInstantwinCampaignAnalyticsMessageSendCountsTab
              :prize-id="prizeId"
              :rows="messageSendCountRows"
              :total="messageSendCountTotal"
              :paging-no="messageSendCountPagingNo"
              :limit-count="messageSendCountLimitCount"
              :sort-by="messageSendCountSortBy"
              :loading="messageSendCountLoading"
              :download-csv="downloadMessageSendCountsCsv"
              @sort-change="handleMessageSendCountsSort"
              @paging="handleMessageSendCountsPaging"
            />
          </el-tab-pane>
        </el-tabs>
        <div class="mt-x2" :gutter="20"></div>
        <div class="public-setting mt-x1" layout layout-align="start-center">
          <div class="public-switch">
            <el-switch
              v-model="isPublicAnalyticsPage"
              :active-text="'分析結果の共有'"
              :active-value="1"
              :inactive-value="0"
              @change="handlePublicFlagsChange('分析結果の共有')"
            ></el-switch>
            <el-tooltip placement="top">
              <div slot="content">{{ 'キャンペーン分析ページを外部公開します。' }}</div>
              <i class="mr-x2 mdi mdi-help-circle"></i>
            </el-tooltip>
          </div>
          <div class="public-url" v-if="isPublicAnalyticsPage">{{ publicPageUrl }}</div>
          <el-button class="ml-x1" v-if="isPublicAnalyticsPage" @click="copyToClipboard(publicPageUrl)">{{
            'コピー'
          }}</el-button>
        </div>
        <div class="public-setting mt-x1" layout layout-align="start-center">
          <div class="public-switch">
            <el-switch
              v-model="isPublicAnalyticsApi"
              :active-text="'APIとして公開'"
              :active-value="1"
              :inactive-value="0"
              @change="handlePublicFlagsChange('APIとして公開')"
            ></el-switch>
            <el-tooltip placement="top">
              <div slot="content">
                {{ 'キャンペーン分析データをAPIとして外部公開します。' }}<br />
                {{ 'entry_countが応募数、applicant_countが応募者数となり、1時間ごとにデータが更新されます。' }}
              </div>
              <i class="mr-x2 mdi mdi-help-circle"></i>
            </el-tooltip>
          </div>
          <div class="public-url" v-if="isPublicAnalyticsApi">{{ publicApiUrl }}</div>
          <el-button class="ml-x1" v-if="isPublicAnalyticsApi" @click="copyToClipboard(publicApiUrl)">{{
            'コピー'
          }}</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import moment, { Moment } from 'moment-timezone'
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, ref, watch } from 'vue'

import AnalyticsChartVertical from '@/client/components/_atoms/analytics_chart_vertical'
import AnalyticsPeriodPicker from '@/client/components/_atoms/analytics_period_picker'
import AnalyticsTotal from '@/client/components/_atoms/analytics_total'
import Icon from '@/client/components/_atoms/basic_icon'
import CsvDownload from '@/client/components/_atoms/csv_download'
import { CAMPAIGN_STATUS } from '@/client/constant'
import Util from '@/client/core/util'
import { TrackingService } from '@/client/services'

import { useInstagramInstantwinCampaignAnalyticsStore } from '../hooks/instagramInstantwinCampaignAnalyticsStore'
import { Interval, MessageSendCountSortBy, MetricSelection } from '../types'
import InstagramInstantwinCampaignAnalyticsEnqueteTab from './InstagramInstantwinCampaignAnalyticsEnqueteTab.vue'
import InstagramInstantwinCampaignAnalyticsMessageSendCountsTab from './InstagramInstantwinCampaignAnalyticsMessageSendCountsTab.vue'

const { proxy } = getCurrentInstance() as any

const visible = ref(false)

const activeTab = ref('campaigns')
const store = useInstagramInstantwinCampaignAnalyticsStore()
const {
  campaign,
  campaignMeta,
  analyticsRows,
  prizeOptions,
  prizeId,
  enqueteQuestions,
  enqueteSelectedId,
  enqueteAnswers,
  enqueteTotalCount,
  messageSendCountRows,
  messageSendCountTotal,
  messageSendCountPagingNo,
  messageSendCountLimitCount,
  messageSendCountSortBy,
  enqueteLoading,
  messageSendCountLoading,
  dateTimeRange,
  interval,
  value1,
  value2,
  isBeginAtZero,
  isPublicAnalyticsPage,
  isPublicAnalyticsApi,
  loading,
  labels,
  analytics,
  entryCount,
  applicantCount,
  newApplicantCount,
  winnerCount,
  followerUpDown
} = storeToRefs(store)

const metricOptions = [
  { label: '指定なし', value: '' },
  { label: '応募数', value: 'entry' },
  { label: '応募者数', value: 'applicant' },
  { label: '新規応募者数', value: 'newApplicant' },
  { label: '当選数', value: 'winner' },
  { label: 'フォロワー', value: 'follower' }
]

const intervals: Array<{ text: string; value: Interval }> = [
  { text: '1時間', value: 'hour' },
  { text: '1日', value: '1_day' },
  { text: '7日', value: '7_day' },
  { text: '1ヶ月', value: '1_month' }
]

const campaignName = computed(() => campaignMeta.value.name ?? '')
const campaignStartDatetime = computed(() => campaign.value?.startDatetime ?? null)
const campaignEndDatetime = computed(() => campaign.value?.endDatetime ?? null)

const isCampaignFinalCollection = computed(() => {
  return campaignMeta.value.status === CAMPAIGN_STATUS.DURING_FINAL_COLLECTION
})

const isCampaignCollectionCompleted = computed(() => {
  return campaignMeta.value.status === CAMPAIGN_STATUS.COLLECTION_COMPLETED
})

const collectCompletedAt = computed(() => {
  if (!campaignMeta.value.endDatetime) {
    return ''
  }

  // 分析バッチの実行完了を考慮し、キャンペーン終了時刻の1時間後を表示する
  return moment(campaignMeta.value.endDatetime).add(1, 'hours').format('YYYY年MM月DD日 HH:mm')
})

const hasAnalyticsData = computed(() => analyticsRows.value.length > 0)

const chartData = computed(() => {
  const data: Array<{ metric: string; label: string; data: Array<number | null> }> = []

  if (value1.value) {
    const selected = metricOptions.find(option => option.value === value1.value)
    if (selected) {
      data.push({
        metric: value1.value,
        label: selected.label,
        data: analytics.value[value1.value as keyof typeof analytics.value]
      })
    }
  }

  if (value2.value) {
    const selected = metricOptions.find(option => option.value === value2.value)
    if (selected) {
      data.push({
        metric: value2.value,
        label: selected.label,
        data: analytics.value[value2.value as keyof typeof analytics.value]
      })
    }
  }

  return data
})

const publicPageUrl = computed(() => {
  if (!campaignMeta.value.hash) {
    return ''
  }

  return `https://${window.location.host}/publics/instagram_instantwin_campaign_analytics?hash=${campaignMeta.value.hash}`
})

const publicApiUrl = computed(() => {
  if (!campaignMeta.value.hash) {
    return ''
  }

  const baseUrl = `https://${window.location.host}/api/publics/applicant_count?hash=${campaignMeta.value.hash}`
  if (prizeId.value) {
    return `${baseUrl}&prizeId=${prizeId.value}`
  }

  return baseUrl
})

type DateRangeValue = [] | [string | Date | Moment, string | Date | Moment] | null

const open = async (campaignId: number) => {
  visible.value = true
  activeTab.value = 'campaigns'
  const loaded = await store.initialize(campaignId)
  if (!loaded) {
    alert()
    close()
  }
}

const close = () => {
  visible.value = false
  store.reset()
}

const handleBeforeClose = () => {
  TrackingService.sendEvent('click:キャンペーン分析（IGIW）|閉じる')
  close()
}

const handleDateRangeChange = async (value: DateRangeValue) => {
  TrackingService.sendEvent('select:キャンペーン分析（IGIW）|カレンダー')

  store.setDateTimeRange(value)
  const loaded = await store.refreshAnalytics()
  if (!loaded) {
    alert()
    close()
  }
  await refreshEnqueteByTab(activeTab.value)
}

const handlePrizeChange = async (value: number | null) => {
  TrackingService.sendEvent('select:キャンペーン分析（IGIW）|全ての応募グループ')

  store.setPrizeId(value)
  const loaded = await store.refreshAnalytics()
  if (!loaded) {
    alert()
    close()
  }
  await refreshEnqueteByTab(activeTab.value)
}

const refreshEnqueteByTab = async (tabName: string) => {
  if (!prizeId.value) {
    return
  }
  if (tabName === 'questionnaire') {
    const loaded = await store.refreshEnquete()
    if (!loaded) {
      alert()
      close()
    }
  }
  if (tabName === 'message_send_counts') {
    const loaded = await store.refreshMessageSendCounts()
    if (!loaded) {
      alert()
      close()
    }
  }
}

const handleEnqueteQuestionChange = async (value: number) => {
  store.setEnqueteSelectedId(value)
  const loaded = await store.refreshEnquete()
  if (!loaded) {
    alert()
    close()
  }
}

const handleMessageSendCountsSort = async (sortBy: MessageSendCountSortBy | '') => {
  store.setMessageSendCountSortBy(sortBy)
  if (!prizeId.value) return
  const loaded = await store.refreshMessageSendCounts()
  if (!loaded) {
    alert()
    close()
  }
}

const handleMessageSendCountsPaging = async ({ currentPage, pageSize }: { currentPage: number; pageSize: number }) => {
  store.setMessageSendCountPaging(currentPage, pageSize)
  if (!prizeId.value) return
  const loaded = await store.refreshMessageSendCounts()
  if (!loaded) {
    alert()
    close()
  }
}

const handleMetricChange = (target: 'value1' | 'value2') => {
  if (target === 'value1') {
    TrackingService.sendEvent('select:キャンペーン分析（IGIW）|値1')
    store.setMetricSelection('value1', value1.value as MetricSelection)
    return
  }

  TrackingService.sendEvent('select:キャンペーン分析（IGIW）|値2')
  store.setMetricSelection('value2', value2.value as MetricSelection)
}

const handleIntervalChange = (value: Interval) => {
  if (value === 'hour') {
    TrackingService.sendEvent('click:キャンペーン分析（IGIW）|1時間')
  } else if (value === '1_day') {
    TrackingService.sendEvent('click:キャンペーン分析（IGIW）|1日')
  } else if (value === '7_day') {
    TrackingService.sendEvent('click:キャンペーン分析（IGIW）|7日')
  } else {
    TrackingService.sendEvent('click:キャンペーン分析（IGIW）|キャンペーン|1ヶ月')
  }

  store.setIntervalValue(value)
}

const handlePublicFlagsChange = async (label: string) => {
  TrackingService.sendEvent(
    `click:キャンペーン分析（IGIW）|${label === '分析結果の共有' ? '分析結果の共有' : 'APIとして公開'}`
  )

  const updated = await store.updatePublicFlags()
  if (!updated) {
    proxy.$notify({
      title: 'キャンペーン分析公開フラグの変更に失敗しました。',
      customClass: 'danger',
      duration: 5000
    })
  }
}

const downloadCsv = () => {
  TrackingService.sendEvent('click:キャンペーン分析（IGIW）|CSVダウンロード')

  const filename = `${campaignName.value}_分析`
  const data: Array<Array<string | number | null>> = []

  data.push(['日付', '応募数', '応募者数', '新規応募者数', '当選数', 'フォロワー'])

  labels.value.forEach((label, index) => {
    let resolvedLabel = label
    if (interval.value === '1_day') {
      resolvedLabel = moment(label).startOf('hour').format('YYYY/MM/DD')
    }

    const applicantValue = analytics.value.applicant[index]
    const followerValue = analytics.value.follower[index]
    const entryValue = analytics.value.entry[index]
    const newApplicantValue = analytics.value.newApplicant[index]
    const winnerValue = analytics.value.winner[index]

    const applicant = typeof applicantValue === 'number' && !isNaN(applicantValue) ? applicantValue : null
    const follower = typeof followerValue === 'number' && !isNaN(followerValue) ? followerValue : null
    const entry = typeof entryValue === 'number' && !isNaN(entryValue) ? entryValue : null
    const newApplicant = typeof newApplicantValue === 'number' && !isNaN(newApplicantValue) ? newApplicantValue : null
    const winner = typeof winnerValue === 'number' && !isNaN(winnerValue) ? winnerValue : null

    data.push([resolvedLabel, entry, applicant, newApplicant, winner, follower])
  })

  return { filename, data }
}

const downloadMessageSendCountsCsv = async () => {
  TrackingService.sendEvent('click:キャンペーン分析（IGIW）|メッセージごとの送信数|CSVダウンロード')

  if (!prizeId.value) {
    return { error: 'NO_DATA' }
  }

  const payload = await store.exportMessageSendCountsCsv()
  const rows = payload?.rows ?? []
  if (!rows.length) {
    return { error: 'NO_DATA' }
  }

  const data: Array<Array<string | number | null>> = []
  data.push(['メッセージ', '種別', '送信数'])
  rows.forEach(row => {
    data.push([row.message || '', row.messageType, row.receivedCount])
  })

  const filename = `${campaignName.value}_メッセージ送信数`
  return { filename, data }
}

const copyToClipboard = (text: string) => {
  Util.copyToClipboard(text)
  proxy.$message({
    message: 'クリップボードにURLをコピーしました。',
    type: 'success'
  })
}

const alert = () => {
  proxy.$notify({
    title: 'キャンペーン分析の取得に失敗しました。',
    message: '恐れ入りますが、時間をおいて再度お試しください。',
    customClass: 'danger',
    duration: 5000
  })
}

const handleBeginAtZeroChange = () => {
  TrackingService.sendEvent('switch:キャンペーン分析（IGIW）|0から表示')
  store.setBeginAtZero(Boolean(isBeginAtZero.value))
}

watch(activeTab, async value => {
  await refreshEnqueteByTab(value)
})

defineExpose({
  open
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.campaign_analytics_dialog {
  :deep {
    .graph-container {
      padding: $spacing-unit ($spacing-unit * 3);
      border: 1px solid $border-color;
    }

    .body {
      margin: 0 ($spacing-unit * 3);
    }

    .custom-title {
      color: $text-color-white;
    }

    .date-time-picker {
      .el-range-editor {
        width: 100% !important;
      }

      float: right;
    }

    .mdi-help-circle {
      margin-left: $spacing-unit;
      font-size: 20px;
    }
  }

  .public-setting {
    height: $spacing-unit * 4;
  }

  .public-switch {
    width: $spacing-unit * 25;
  }

  .public-url {
    width: $spacing-unit * 60;
    padding: 0 $spacing-unit;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    background-color: $background-color;
  }
}
</style>
